/**
 * LaborDashboard — Labor vs Productivity visualization with date range picker.
 *
 * Data strategy:
 *   - Initial render: uses payrollData + entries props (already loaded by Dashboard, no flicker)
 *   - Date range change: fetches fresh data via the same existing endpoints, self-contained
 *
 * Endpoints reused:
 *   GET /api/timesheets/payroll-summary?start_date=&end_date=
 *   GET /api/timesheets?start_date=&end_date=
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { timesheetAPI, restaurantAPI } from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';
import { Card, CardContent } from './ui/card';
import { EvaInsights } from './EvaInsights';
import { Button } from './ui/button';
import { DollarSign, Clock, TrendingUp, AlertTriangle, CheckCircle, Users, Loader2, Calendar, Download, Info } from 'lucide-react';

// ── CSV export ───────────────────────────────────────────────────────────────

function csvCell(val) {
  const s = String(val ?? '');
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? `"${s.replace(/"/g, '""')}"`
    : s;
}

function generateCSV(employees, periodTotals, activeRange, locationName, formatMoney) {
  const avgCostPH = (periodTotals.total_hours ?? 0) > 0
    ? (periodTotals.total_pay ?? 0) / periodTotals.total_hours
    : 0;

  const meta = [
    ['Labor Report'],
    ['Date Range', `${activeRange.start} to ${activeRange.end}`],
    ['Location', locationName],
    ['Total Labor Cost', periodTotals.total_pay != null ? formatMoney(periodTotals.total_pay) : '—'],
    ['Total Hours', periodTotals.total_hours ?? 0],
    ['Avg Cost / Hour', avgCostPH > 0 ? formatMoney(avgCostPH) : '—'],
    [],
    ['Employee Name', 'Location', 'Total Hours', 'Regular Hours', 'Overtime Hours',
     'Holiday Hours', 'Unpaid Break', 'Hourly Wage', 'Total Pay'],
  ];

  const rows = employees.map(e => [
    e.employee_name,
    e.location || '',
    e.total_hours ?? 0,
    e.regular_hours ?? 0,
    e.overtime_hours ?? 0,
    e.holiday_hours ?? 0,
    e.unpaid_hours ?? 0,
    e.hourly_wage ?? '',
    e.total_pay ?? '',
  ]);

  return [...meta, ...rows]
    .map(row => row.map(csvCell).join(','))
    .join('\n');
}

// ── Date helpers ──────────────────────────────────────────────────────────────

const isoDate = d => d.toISOString().split('T')[0];
const today = () => isoDate(new Date());

function getPresetRange(key) {
  const now = new Date();
  switch (key) {
    case 'this_week': {
      const mon = new Date(now);
      mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return { start: isoDate(mon), end: isoDate(sun) };
    }
    case 'last_week': {
      const mon = new Date(now);
      mon.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return { start: isoDate(mon), end: isoDate(sun) };
    }
    case 'biweekly': {
      const start = new Date(now);
      start.setDate(now.getDate() - 13);
      return { start: isoDate(start), end: today() };
    }
    case 'this_month': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { start: isoDate(first), end: isoDate(last) };
    }
    case 'last_month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: isoDate(first), end: isoDate(last) };
    }
    default:
      return null;
  }
}

const PRESETS = [
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'biweekly', label: 'Bi-weekly' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom', label: 'Custom' },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, sub, color = 'teal', testId, helpHref, helpTitle }) {
  const colors = {
    teal:   'from-teal-50 to-emerald-50 border-teal-200',
    amber:  'from-amber-50 to-orange-50 border-amber-200',
    violet: 'from-violet-50 to-purple-50 border-violet-200',
    slate:  'from-slate-50 to-slate-100 border-slate-200',
    red:    'from-red-50 to-rose-50 border-red-200',
  };
  const iconColors = {
    teal: 'text-teal-500', amber: 'text-amber-500', violet: 'text-violet-500', slate: 'text-slate-400', red: 'text-red-500',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-4 ${colors[color]}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
        <div className="flex items-center gap-1.5">
          {helpHref && (
            <a
              href={helpHref}
              target="_blank"
              rel="noopener noreferrer"
              title={helpTitle || 'Learn more'}
              className="text-slate-400 hover:text-slate-700 transition-colors"
              data-testid={testId ? `${testId}-help` : undefined}
            >
              <Info size={13} />
            </a>
          )}
          <Icon size={16} className={iconColors[color]} />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function fmt(h) {
  if (h == null) return '—';
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return hrs > 0 ? (mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`) : `${mins}m`;
}

const CustomBarTooltip = ({ active, payload, formatMoney }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-800">{d.fullName}</p>
      <p className="text-teal-700">Cost: <strong>{formatMoney(d.cost)}</strong></p>
      <p className="text-slate-500">Hours: {fmt(d.hours)}</p>
      {d.isHighCost && <p className="text-orange-600 mt-0.5">High cost</p>}
      {d.isOTHeavy && <p className="text-amber-600">Overtime heavy</p>}
    </div>
  );
};

const CustomLineTooltip = ({ active, payload, label, formatMoney, hasWages }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {hasWages && <p className="text-teal-700">Cost: <strong>{formatMoney(payload[0]?.value ?? 0)}</strong></p>}
      <p className="text-slate-500">Hours: <strong>{fmt(payload[hasWages ? 1 : 0]?.value ?? 0)}</strong></p>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export function LaborDashboard({ payrollData: initPayroll, entries: initEntries = [] }) {
  const { formatMoney } = useCurrency();

  // ── Date range state ─────────────────────────────────────────────────────
  const [preset, setPreset] = useState('this_week');
  const [customStart, setCustomStart] = useState(() => getPresetRange('this_week').start);
  const [customEnd, setCustomEnd] = useState(() => isoDate(new Date()));

  // ── Location filter state ────────────────────────────────────────────────
  const [locationId, setLocationId] = useState('all');
  const [locations, setLocations] = useState([]);

  // Fetch available locations once on mount
  useEffect(() => {
    restaurantAPI.getAll()
      .then(res => setLocations(res.data || []))
      .catch(() => {});
  }, []);

  // ── Data state (bootstrapped from props) ─────────────────────────────────
  const [laborPayroll, setLaborPayroll] = useState(initPayroll || { employees: [], period_totals: {} });
  const [laborEntries, setLaborEntries] = useState(initEntries);
  const [loading, setLoading] = useState(false);

  // Skip first render — use props (Dashboard already fetched this week's data)
  const isFirstRender = useRef(true);

  // ── Fetch on date range change ────────────────────────────────────────────
  const fetchLaborData = useCallback(async (start, end, locId) => {
    if (!start || !end || start > end) return;
    setLoading(true);
    const params = { start_date: start, end_date: end };
    if (locId && locId !== 'all') params.location_id = locId;
    try {
      const [payrollRes, entriesRes] = await Promise.all([
        timesheetAPI.getPayrollSummary(params),
        timesheetAPI.getEntries(params),
      ]);
      setLaborPayroll(payrollRes.data || { employees: [], period_totals: {} });
      setLaborEntries(entriesRes.data || []);
    } catch {
      // silently keep previous data on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Active date range
  const activeRange = useMemo(() => {
    if (preset !== 'custom') return getPresetRange(preset) || { start: customStart, end: customEnd };
    return { start: customStart, end: customEnd };
  }, [preset, customStart, customEnd]);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    // Debounce custom date inputs — presets and location changes fire immediately
    const delay = preset === 'custom' ? 500 : 0;
    const t = setTimeout(() => fetchLaborData(activeRange.start, activeRange.end, locationId), delay);
    return () => clearTimeout(t);
  }, [activeRange.start, activeRange.end, preset, locationId, fetchLaborData]);

  // ── Derived metrics ───────────────────────────────────────────────────────
  const { employees = [], period_totals = {} } = laborPayroll;
  const hasWages = employees.some(e => e.hourly_wage != null);

  const totalCost  = period_totals.total_pay   ?? 0;
  const totalHours = period_totals.total_hours ?? 0;
  const avgCostPH  = totalHours > 0 ? totalCost / totalHours : 0;
  const avgCostPE  = employees.filter(e => e.total_pay != null).length > 0
    ? totalCost / employees.filter(e => e.total_pay != null).length
    : 0;
  const highestEmp = useMemo(
    () => [...employees].sort((a, b) => (b.total_pay ?? 0) - (a.total_pay ?? 0))[0] ?? null,
    [employees]
  );

  const wageMap = useMemo(() => {
    const m = {};
    employees.forEach(e => { if (e.employee_id) m[e.employee_id] = e.hourly_wage; });
    return m;
  }, [employees]);

  const empBarData = useMemo(() =>
    employees
      .filter(e => e.total_hours > 0)
      .sort((a, b) => hasWages
        ? (b.total_pay ?? 0) - (a.total_pay ?? 0)
        : b.total_hours - a.total_hours)
      .map(e => ({
        name: e.employee_name.split(' ')[0],
        fullName: e.employee_name,
        cost: parseFloat((e.total_pay ?? 0).toFixed(2)),
        hours: e.total_hours,
        barValue: hasWages && e.hourly_wage != null ? (e.total_pay ?? 0) : e.total_hours,
        isHighCost: hasWages && e.hourly_wage != null && (e.total_pay ?? 0) > avgCostPE * 1.2,
        isOTHeavy: (e.overtime_hours ?? 0) > 0,
      })),
    [employees, avgCostPE, hasWages]
  );

  const dailyData = useMemo(() => {
    const map = {};
    laborEntries.forEach(e => {
      if (!e.date || !(e.duration_hours > 0)) return;
      const wage = wageMap[e.employee_id] ?? null;
      if (!map[e.date]) map[e.date] = { date: e.date, cost: 0, hours: 0 };
      map[e.date].hours += e.duration_hours;
      if (wage != null) map[e.date].cost += e.duration_hours * wage;
    });
    return Object.values(map)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(d => ({
        label: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        cost: parseFloat(d.cost.toFixed(2)),
        hours: parseFloat(d.hours.toFixed(2)),
      }));
  }, [laborEntries, wageMap]);

  const insights = useMemo(() =>
    employees.map(e => {
      const flags = [];
      if (hasWages && e.hourly_wage != null && (e.total_pay ?? 0) > avgCostPE * 1.2)
        flags.push({ type: 'high_cost', label: 'High cost', color: 'text-orange-700 bg-orange-50 border-orange-200' });
      if ((e.overtime_hours ?? 0) > 0)
        flags.push({ type: 'ot_heavy', label: 'Overtime heavy', color: 'text-amber-700 bg-amber-50 border-amber-200' });
      return { ...e, flags };
    }).filter(e => e.flags.length > 0),
    [employees, avgCostPE, hasWages]
  );

  const barChartW = Math.max(300, empBarData.length * 64);

  // ── Labor insights (overtime cost, tips, missed breaks) ─────────────────
  const [insightsData, setInsightsData] = useState(null);
  useEffect(() => {
    const params = { start_date: activeRange.start, end_date: activeRange.end };
    if (locationId && locationId !== 'all') params.location_id = locationId;
    timesheetAPI.getLaborInsights(params)
      .then(res => setInsightsData(res.data))
      .catch(() => setInsightsData(null));
  }, [activeRange.start, activeRange.end, locationId]);


  // ── CSV export handler ────────────────────────────────────────────────────
  const handleExportCSV = useCallback(() => {
    if (employees.length === 0) return;
    const locationName = locationId === 'all'
      ? 'All Locations'
      : (locations.find(l => l.id === locationId)?.location_name || locationId);
    const csv = generateCSV(employees, period_totals, activeRange, locationName, formatMoney);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `labor-report-${isoDate(new Date())}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [employees, period_totals, activeRange, locationId, locations, formatMoney]);

  // ── Empty state ───────────────────────────────────────────────────────────
  const isEmpty = employees.length === 0 && laborEntries.length === 0;

  return (
    <div className="space-y-4" data-testid="labor-dashboard">

      {/* ── Header + Date range picker ── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Labor Overview
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {activeRange.start} → {activeRange.end}
            </p>
          </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 size={16} className="animate-spin text-teal-500" data-testid="labor-loading-spinner" />}
          {!isEmpty && (
            <button
              onClick={handleExportCSV}
              disabled={loading || employees.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 bg-white hover:border-teal-400 hover:text-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="labor-export-csv-btn"
              title="Download CSV"
            >
              <Download size={12} />
              Export CSV
            </button>
          )}
        </div>
        </div>

        {/* Preset pills */}
        <div className="flex flex-wrap gap-1.5" data-testid="labor-preset-buttons">
          {PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => {
                setPreset(p.key);
                if (p.key !== 'custom') {
                  const r = getPresetRange(p.key);
                  if (r) { setCustomStart(r.start); setCustomEnd(r.end); }
                }
              }}
              data-testid={`preset-${p.key}`}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                preset === p.key
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-teal-400 hover:text-teal-700'
              }`}
            >
              {p.key === 'custom' && <Calendar size={10} className="inline mr-1" />}
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom date inputs */}
        {preset === 'custom' && (
          <div className="flex flex-col sm:flex-row gap-2" data-testid="custom-date-inputs">
            <div className="flex-1">
              <label className="text-xs text-slate-400 block mb-1">From</label>
              <input
                type="date"
                value={customStart}
                max={customEnd || today()}
                onChange={e => setCustomStart(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                data-testid="custom-start-date"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-slate-400 block mb-1">To</label>
              <input
                type="date"
                value={customEnd}
                min={customStart}
                max={today()}
                onChange={e => setCustomEnd(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                data-testid="custom-end-date"
              />
            </div>
          </div>
        )}

        {/* Location filter */}
        {locations.length > 0 && (
          <div data-testid="location-filter-row">
            <label className="text-xs text-slate-400 block mb-1">Location</label>
            <select
              value={locationId}
              onChange={e => setLocationId(e.target.value)}
              className="w-full sm:w-auto min-w-[200px] border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
              data-testid="location-dropdown"
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id} data-testid={`location-option-${loc.id}`}>
                  {loc.location_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Loading overlay or empty state ── */}
      {isEmpty && !loading && (
        <Card className="border-slate-200" data-testid="labor-empty-state">
          <CardContent className="py-14 text-center">
            <DollarSign className="mx-auto h-10 w-10 text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No labor data for this period</p>
            <p className="text-slate-400 text-sm mt-1">Try a different date range or ensure shifts are clocked out</p>
          </CardContent>
        </Card>
      )}

      {/* ── KPI cards ── */}
      {!isEmpty && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="labor-kpi-cards">
          <KpiCard
            icon={DollarSign}
            label="Total Labor"
            value={hasWages ? formatMoney(totalCost) : '—'}
            sub={hasWages ? undefined : 'Set wages in Staff'}
            color="teal"
            testId="kpi-total-labor"
          />
          <KpiCard
            icon={Clock}
            label="Total Hours"
            value={fmt(totalHours)}
            sub={`${employees.length} employee${employees.length !== 1 ? 's' : ''}`}
            color="slate"
            testId="kpi-total-hours"
          />
          <KpiCard
            icon={TrendingUp}
            label="Avg Cost / Hour"
            value={hasWages && avgCostPH > 0 ? formatMoney(avgCostPH) : '—'}
            sub={hasWages ? 'blended rate' : undefined}
            color="amber"
            testId="kpi-avg-cost-hour"
          />
          <KpiCard
            icon={Users}
            label="Highest Cost"
            value={highestEmp && hasWages && highestEmp.total_pay != null
              ? formatMoney(highestEmp.total_pay)
              : highestEmp ? fmt(highestEmp.total_hours) : '—'}
            sub={highestEmp?.employee_name?.split(' ')[0]}
            color="violet"
            testId="kpi-highest-cost"
          />
        </div>
      )}

      {/* ── Insights strip: overtime cost / tips / bonuses / missed breaks ── */}
      {!isEmpty && insightsData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="labor-insights-strip">
          <KpiCard
            icon={AlertTriangle}
            label="Overtime Cost"
            value={hasWages ? formatMoney(insightsData.overtime_cost || 0) : '—'}
            sub={hasWages ? 'this period' : 'Set wages in Staff'}
            color="amber"
            testId="kpi-overtime-cost"
          />
          <KpiCard
            icon={DollarSign}
            label="Tips Paid"
            value={formatMoney(insightsData.tips_total || 0)}
            sub="this period"
            color="teal"
            testId="kpi-tips-total"
          />
          <KpiCard
            icon={DollarSign}
            label="Bonuses Paid"
            value={formatMoney(insightsData.bonus_total || 0)}
            sub="KPI bonuses"
            color="violet"
            testId="kpi-bonus-total"
            helpHref="/help/kpi-bonus#scoring"
            helpTitle="How is this calculated? See the KPI scoring rules"
          />
          <KpiCard
            icon={CheckCircle}
            label="Missed Breaks"
            value={String(insightsData.missed_breaks || 0)}
            sub={(insightsData.missed_breaks || 0) === 0 ? 'all compliant' : 'shifts flagged'}
            color={(insightsData.missed_breaks || 0) > 0 ? 'red' : 'slate'}
            testId="kpi-missed-breaks"
          />
        </div>
      )}

      {/* ── Supervisor KPI strip (only when KPI data has been recorded) ── */}
      {insightsData?.kpi?.tracked_shifts > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="supervisor-kpi-strip">
          <KpiCard
            icon={TrendingUp}
            label="Avg KPI Score"
            value={String(insightsData.kpi.avg_score ?? '—')}
            sub={`${insightsData.kpi.tracked_shifts} shift${insightsData.kpi.tracked_shifts === 1 ? '' : 's'} tracked`}
            color={insightsData.kpi.avg_score >= 85 ? 'teal' : insightsData.kpi.avg_score >= 70 ? 'amber' : 'red'}
            testId="kpi-avg-score"
            helpHref="/help/kpi-bonus#scoring"
            helpTitle="How is the KPI score calculated?"
          />
          <KpiCard
            icon={CheckCircle}
            label="Bonus Eligible"
            value={`${insightsData.kpi.bonus_eligible_pct ?? 0}%`}
            sub="of tracked shifts"
            color={(insightsData.kpi.bonus_eligible_pct ?? 0) >= 50 ? 'teal' : 'amber'}
            testId="kpi-bonus-pct"
            helpHref="/help/kpi-bonus#scoring"
            helpTitle="What makes a shift bonus eligible?"
          />
          <KpiCard
            icon={AlertTriangle}
            label="Failed Shifts"
            value={String(insightsData.kpi.failed_shifts || 0)}
            sub={(insightsData.kpi.failed_shifts || 0) === 0 ? 'all clean' : 'KPI < 70'}
            color={(insightsData.kpi.failed_shifts || 0) > 0 ? 'red' : 'slate'}
            testId="kpi-failed-shifts"
            helpHref="/help/kpi-bonus#scoring"
            helpTitle="Why is a shift flagged as failed?"
          />
        </div>
      )}

      {/* ── Eva Insights ── */}
      {!isEmpty && (
        <EvaInsights
          employees={employees}
          periodTotals={period_totals}
          dailyData={dailyData}
          locationId={locationId}
        />
      )}

      {/* ── Cost per employee — bar chart ── */}
      {empBarData.length > 0 && (
        <Card className="border-slate-200" data-testid="emp-cost-chart-card">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              {hasWages ? 'Cost per Employee' : 'Hours per Employee'}
            </p>
            <div className="overflow-x-auto">
              <div style={{ width: barChartW, height: 200 }}>
                <BarChart
                  width={barChartW}
                  height={200}
                  data={empBarData}
                  barSize={36}
                  margin={{ top: 4, right: 8, left: hasWages ? -4 : -16, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => hasWages ? `$${v}` : `${v}h`}
                    width={40}
                  />
                  <Tooltip content={<CustomBarTooltip formatMoney={formatMoney} />} cursor={{ fill: 'rgba(20,184,166,0.06)' }} />
                  <Bar dataKey="barValue" radius={[5, 5, 0, 0]}>
                    {empBarData.map((d, i) => (
                      <Cell key={`emp-${i}`} fill={d.isHighCost ? '#f97316' : '#14b8a6'} data-testid={`emp-bar-${i}`} />
                    ))}
                  </Bar>
                </BarChart>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Daily cost — line chart ── */}
      {dailyData.length > 1 && (
        <Card className="border-slate-200" data-testid="daily-cost-chart-card">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              {hasWages ? 'Estimated Daily Labor Cost' : 'Daily Hours Worked'}
              {hasWages && <span className="ml-1.5 font-normal text-slate-400 normal-case">(approx.)</span>}
            </p>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={dailyData} margin={{ top: 4, right: 8, left: hasWages ? -4 : -16, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <YAxis
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={v => hasWages ? `$${v}` : `${v}h`}
                  width={40}
                />
                <Tooltip content={<CustomLineTooltip formatMoney={formatMoney} hasWages={hasWages} />} />
                {hasWages && (
                  <Line
                    dataKey="cost"
                    stroke="#14b8a6"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: '#14b8a6' }}
                    activeDot={{ r: 5 }}
                    data-testid="daily-cost-line"
                  />
                )}
                <Line
                  dataKey="hours"
                  stroke={hasWages ? '#f97316' : '#14b8a6'}
                  strokeWidth={hasWages ? 1.5 : 2.5}
                  strokeDasharray={hasWages ? '4 3' : undefined}
                  dot={{ r: 2, fill: hasWages ? '#f97316' : '#14b8a6' }}
                  activeDot={{ r: 4 }}
                  data-testid="daily-hours-line"
                />
              </LineChart>
            </ResponsiveContainer>
            {hasWages && (
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-5 h-0.5 bg-teal-500 rounded" /> Cost
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-5 border-t border-dashed border-orange-400" /> Hours
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Insights ── */}
      {!isEmpty && (
        <Card className="border-slate-200" data-testid="labor-insights-card">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Insights</p>
            {insights.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700 py-1" data-testid="insights-all-clear">
                <CheckCircle size={15} className="text-emerald-500 shrink-0" />
                No issues detected — labor distribution looks normal.
              </div>
            ) : (
              <div className="space-y-2" data-testid="insights-list">
                {insights.map((emp, i) => (
                  <div key={emp.employee_id || i} className="flex flex-wrap items-center gap-2 py-1" data-testid={`insight-row-${i}`}>
                    <span className="text-sm font-medium text-slate-800 min-w-[100px]">{emp.employee_name}</span>
                    {emp.flags.map(f => (
                      <span
                        key={f.type}
                        className={`inline-flex items-center gap-1 text-xs font-medium border rounded-full px-2.5 py-0.5 ${f.color}`}
                        data-testid={`flag-${f.type}-${i}`}
                      >
                        <AlertTriangle size={10} />
                        {f.label}
                      </span>
                    ))}
                    {emp.total_pay != null && (
                      <span className="text-xs text-slate-400 ml-auto">{formatMoney(emp.total_pay)} · {fmt(emp.total_hours)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

    </div>
  );
}
